import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import classNames from 'classnames';

import styles from './CollapsibleCard.module.css';

import { Card, type CardProps } from '../Card';
import { Divider } from '../Divider';
import type { TextProps } from '../Text';
import { Text } from '../Text';
import { CollapsibleCardTransition } from './CollapsibleCardTransition';
import { ToggleButton } from './ToggleButton';

export const getId = (
  type: 'section' | 'label',
  collapsibleCardId: string | undefined,
  key: number | string
): string | undefined =>
  collapsibleCardId
    ? `collapsiblecard-${collapsibleCardId}-${type}-${key}`
    : undefined;

export interface CollapsibleCardProps extends CardProps {
  textId?: string;
  heading: ReactNode;
  open?: boolean;
  children: ReactNode;
  /**
   * A function that will be called when the toggle button is clicked. Includes
   * the open state of the card after the toggle.
   */
  onToggle?: (isOpening: boolean) => void;
}

/**
 * Render a card that can be expanded or collapsed.
 *
 * @see https://corgi-x.tfd.engineering/components/collapsiblecard
 */
export function CollapsibleCard(props: CollapsibleCardProps) {
  const { children, heading, open, textId, onToggle, ...cardProps } = props;

  const collapsibleCardId = useId();
  const headingId = getId('label', collapsibleCardId, 0);
  const sectionId = getId('section', collapsibleCardId, 0);
  const [isOpen, setIsOpen] = useState(open);

  const textProps: TextProps = {
    bold: true,
    color: 'Kale3',
    variant: 'heading-28',
    // prop meant for adding an id to the text element if needed for `aria-labelledby`
    ...(textId && { id: textId }),
  };

  const handleToggleClick = () => {
    onToggle?.(!isOpen);

    setIsOpen(!isOpen);
  };

  return (
    <Card
      as="figure"
      aria-labelledby={headingId}
      className={classNames(styles.container, cardProps.className)}
      {...cardProps}
    >
      <ToggleButton
        onClick={handleToggleClick}
        id={headingId}
        open={Boolean(isOpen)}
        sectionId={getId('section', collapsibleCardId, 0)}
      >
        {typeof heading === 'string' ? (
          <Text {...textProps}>{heading}</Text>
        ) : (
          heading
        )}
      </ToggleButton>

      <CollapsibleCardTransition in={Boolean(isOpen)}>
        <Divider
          width={1}
          orientation="horizontal"
          spacing={24}
          color="Charcoal0"
        />
      </CollapsibleCardTransition>

      <CollapsibleCardTransition in={Boolean(isOpen)}>
        <section id={sectionId} aria-labelledby={headingId}>
          {children}
        </section>
      </CollapsibleCardTransition>
    </Card>
  );
}
